import { useEffect, useRef } from "react";
import * as THREE from "three";

const PALETTE = ["#eba6c6", "#bea7f2", "#8fbcff", "#ffd9ea", "#d8ccff"];

/**
 * A slow field of luminous glass motes behind the page — light seen through
 * deep water. Purely ambient: it never responds to content, only to the
 * pointer and to scroll, and it renders a single still frame when the visitor
 * prefers reduced motion.
 */
export function GlassField() {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(56, 1, 0.1, 120);
    camera.position.set(0, 0, 15);

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: "high-performance",
      });
    } catch {
      return; // no WebGL — the CSS gradients carry the page on their own
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    const canvas = renderer.domElement;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    host.appendChild(canvas);

    // --- a soft radial sprite, drawn once on a 2D canvas -------------------
    const sprite = (() => {
      const size = 128;
      const c = document.createElement("canvas");
      c.width = size;
      c.height = size;
      const ctx = c.getContext("2d")!;
      const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      g.addColorStop(0, "rgba(255,255,255,1)");
      g.addColorStop(0.28, "rgba(255,255,255,0.55)");
      g.addColorStop(0.62, "rgba(255,255,255,0.12)");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, size, size);
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    })();

    const isNarrow = window.innerWidth < 760;
    const COUNT = isNarrow ? 150 : 380;
    const bounds = { x: 24, y: 15, z: 18 };

    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const speeds = new Float32Array(COUNT);
    const phase = new Float32Array(COUNT);
    const tint = new THREE.Color();

    for (let i = 0; i < COUNT; i += 1) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * bounds.x * 2;
      positions[i3 + 1] = (Math.random() - 0.5) * bounds.y * 2;
      positions[i3 + 2] = (Math.random() - 0.5) * bounds.z - 2;

      tint.set(PALETTE[i % PALETTE.length]!);
      tint.lerp(new THREE.Color("#ffffff"), Math.random() * 0.45);
      colors[i3] = tint.r;
      colors[i3 + 1] = tint.g;
      colors[i3 + 2] = tint.b;

      speeds[i] = 0.06 + Math.random() * 0.16;
      phase[i] = Math.random() * Math.PI * 2;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    const moteMaterial = new THREE.PointsMaterial({
      size: 0.62,
      map: sprite,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    const motes = new THREE.Points(geometry, moteMaterial);

    // --- out-of-focus coloured light, like bokeh through frosted glass -----
    const bokeh = new THREE.Group();
    const bokehMaterials: THREE.SpriteMaterial[] = [];
    for (let i = 0; i < 5; i += 1) {
      const material = new THREE.SpriteMaterial({
        map: sprite,
        color: new THREE.Color(PALETTE[i % 3]!),
        transparent: true,
        opacity: 0.16,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      bokehMaterials.push(material);
      const glow = new THREE.Sprite(material);
      glow.position.set((Math.random() - 0.5) * 30, (Math.random() - 0.5) * 18, -8 - Math.random() * 10);
      const scale = 13 + Math.random() * 14;
      glow.scale.set(scale, scale, 1);
      bokeh.add(glow);
    }

    const world = new THREE.Group();
    world.add(motes, bokeh);
    scene.add(world);

    // --- input ------------------------------------------------------------
    const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
    const onPointerMove = (event: PointerEvent) => {
      pointer.tx = event.clientX / window.innerWidth - 0.5;
      pointer.ty = event.clientY / window.innerHeight - 0.5;
    };
    let scrollY = window.scrollY;
    const onScroll = () => {
      scrollY = window.scrollY;
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    // --- sizing -----------------------------------------------------------
    const resize = () => {
      const w = host.clientWidth || window.innerWidth;
      const h = host.clientHeight || window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);

    // --- loop -------------------------------------------------------------
    const timer = new THREE.Timer();
    timer.connect(document);
    let frame = 0;
    let running = true;

    const render = () => {
      timer.update();
      const t = timer.getElapsed();

      if (!reduceMotion) {
        const array = geometry.attributes.position!.array as Float32Array;
        for (let i = 0; i < COUNT; i += 1) {
          const i3 = i * 3;
          const ny = (array[i3 + 1] ?? 0) + (speeds[i] ?? 0) * 0.012;
          const nx = (array[i3] ?? 0) + Math.sin(t * 0.18 + (phase[i] ?? 0)) * 0.004;
          array[i3 + 1] = ny > bounds.y ? -bounds.y : ny;
          array[i3] = nx > bounds.x ? -bounds.x : nx < -bounds.x ? bounds.x : nx;
        }
        geometry.attributes.position!.needsUpdate = true;
      }

      pointer.x += (pointer.tx - pointer.x) * 0.045;
      pointer.y += (pointer.ty - pointer.y) * 0.045;

      world.rotation.y = pointer.x * 0.22 + Math.sin(t * 0.04) * 0.04;
      world.rotation.x = -pointer.y * 0.14;
      world.rotation.z = Math.sin(t * 0.03) * 0.02;

      camera.position.y = scrollY * 0.0016;
      camera.position.x = pointer.x * 1.1;
      camera.lookAt(0, camera.position.y * 0.35, 0);

      bokeh.position.y = Math.sin(t * 0.07) * 0.7;

      renderer.render(scene, camera);
    };

    const tick = () => {
      if (!running) return;
      if (!document.hidden) render();
      frame = requestAnimationFrame(tick);
    };

    if (reduceMotion) {
      render();
    } else {
      frame = requestAnimationFrame(tick);
    }

    return () => {
      running = false;
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
      timer.disconnect();
      geometry.dispose();
      moteMaterial.dispose();
      bokehMaterials.forEach((material) => material.dispose());
      sprite.dispose();
      renderer.dispose();
      if (canvas.parentNode === host) host.removeChild(canvas);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10 h-screen w-screen overflow-hidden"
    />
  );
}
